import React, { useContext } from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import useModalFocusTrap from 'components/Hooks/useModalFocusTrap';
import { Link } from 'react-router-dom';
import _get from 'lodash/get';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { capitalizeFirstLetter, getImageFile } from 'utils/stringUtils';
import TakeArrow from 'components/HeroLanding/images/take-arrow-blue.svg';
import NavBarContext from '../../navBarContext';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    padding: '30px 30px 30px 22px',
    gap: 32,
  },
  column: {
    width: 270,
    '& h3': {
      fontSize: 17,
      color: '#4B4B4B',
    },
    '& ul': {
      display: 'flex',
      flexDirection: 'column',
      listStyleType: 'none',
      margin: 0,
      padding: 0,
    },
    '& li': {
      padding: 8,
      borderRadius: 4,
      '& h3': {
        marginTop: '0',
      },
      '& a': {
        color: '#4B4B4B !important',
      },
      '&:hover': {
        fill: '#01619B',
        cursor: 'pointer',
        background: '#EEF9FF',
        '& a': {
          color: '#01619B !important',
        },
      },
    },
    '& a': {
      fontSize: 15,
      fontFamily: 'Mada',
      fontWeight: 400,
      color: '#01619B',
      '&:hover': {
        color: '#1073b1',
      },
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20,
    marginLeft: 8,
  },
  hr: {
    border: 0,
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: 8,
  },
  hrSmallPadding: {
    border: 0,
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '12px 0',
  },
  image: {
    width: '100%',
    objectFit: 'cover',
    height: 150,
    marginBottom: 20,
  },
  badgeContainer: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    fontSize: 12,
    marginBottom: 8,
    color: '#4B4B4B',
    '& img': {
      marginBottom: 2,
    },
  },
  seeAllLink: {
    display: 'flex',
    textTransform: 'uppercase',
    fontSize: '14px !important',
    justifyContent: 'end',
    color: '#01619B',
    '& img': {
      marginLeft: 4,
      marginBottom: 1,
    },
  },
});

const getTypeTitle = (type, siteCopy) => {
  const formattedTitle =
    type !== 'faq' ? capitalizeFirstLetter(type) : type.toUpperCase();
  return _get(
    siteCopy,
    ['pageCopy', 'resourceCardTypeMapping', formattedTitle],
    formattedTitle,
  );
};

function Featured({ onClose }) {
  const classes = useStyles();
  const { siteCopy, news, blogs, sendMixpanelEvent } = useContext(
    NavBarContext,
  );
  const { refCallback } = useModalFocusTrap(onClose, 'featured');
  const [featuredSiteCopy, resourcesSiteCopy] = useSiteCopySelector([
    'featured-navigation',
    'resource-item-page',
  ]);

  const sortedNews = [...news].sort(
    (a, b) =>
      new Date(b.fields?.publicationDate) - new Date(a.fields?.publicationDate),
  );
  const sortedBlogs = [...blogs].sort(
    (a, b) =>
      new Date(b.fields?.publicationDate) - new Date(a.fields?.publicationDate),
  );

  const latestNews = sortedNews[0];
  const latestBlog = sortedBlogs[0];
  const newsImage = getImageFile(latestNews?.fields);
  const blogImage = getImageFile(latestBlog?.fields);

  const handleClick = (subtype, name) => {
    sendMixpanelEvent('Menu Item - Clicked', {
      type: 'Featured',
      subtype,
      name,
    });
  };

  return (
    <Box className={classes.root} ref={refCallback}>
      <Box className={classes.column}>
        <Box className={classes.header}>
          <Typography variant="h3">
            {siteCopy?.pageCopy?.['submenu-featured']?.trending}
          </Typography>
        </Box>
        <Box component="ul" className={classes.list}>
          {featuredSiteCopy.pageCopy?.trending?.map(
            ({ type, title, slug, icon }, index) => (
              <React.Fragment key={slug}>
                <Box component="li">
                  <Box className={classes.badgeContainer}>
                    <img src={icon} alt="" />
                    {getTypeTitle(type, resourcesSiteCopy)}
                  </Box>
                  <Link
                    to={`/${type}/${slug}`}
                    onClick={() => handleClick('Trending', title)}
                  >
                    {title}
                  </Link>
                </Box>
                {index !== featuredSiteCopy.pageCopy?.trending.length - 1 && (
                  <hr className={classes.hr} />
                )}
              </React.Fragment>
            ),
          )}
        </Box>
      </Box>
      {latestNews && (
        <Box className={classes.column}>
          <Box className={classes.list}>
            <Link
              to={`/articles/${latestNews.fields.slug}`}
              onClick={() => handleClick('News', latestNews.fields.name)}
              aria-label={latestNews.fields.name}
            >
              <img className={classes.image} src={newsImage} alt="" />
            </Link>
            <Box className={classes.badgeContainer}>
              <img src={featuredSiteCopy?.pageCopy?.newsIcon} alt="" />
              {featuredSiteCopy?.pageCopy?.featuredNews}
            </Box>
            <Link
              to={`/articles/${latestNews.fields.slug}`}
              onClick={() => handleClick('News', latestNews.fields.name)}
            >
              {latestNews.fields.name}
            </Link>
            <hr className={classes.hrSmallPadding} />
            <Link to="/news" className={classes.seeAllLink}>
              {siteCopy?.pageCopy?.['submenu-featured']?.seeAllNews}
              <img src={TakeArrow} alt="" />
            </Link>
          </Box>
        </Box>
      )}
      {latestBlog && (
        <Box className={classes.column}>
          <Box className={classes.list}>
            <Link
              to={`/articles/${latestBlog.fields.slug}`}
              onClick={() => handleClick('Insights', latestBlog.fields.name)}
              aria-label={latestBlog.fields.name}
            >
              <img className={classes.image} src={blogImage} alt="" />
            </Link>
            <Box className={classes.badgeContainer}>
              <img src={featuredSiteCopy?.pageCopy?.articleIcon} alt="" />
              {featuredSiteCopy?.pageCopy?.featuredArticle}
            </Box>
            <Link
              to={`/articles/${latestBlog.fields.slug}`}
              onClick={() => handleClick('Insights', latestBlog.fields.name)}
            >
              {latestBlog.fields.name}
            </Link>
            <hr className={classes.hrSmallPadding} />
          </Box>
          <Link to="/insights" className={classes.seeAllLink}>
            {siteCopy?.pageCopy?.['submenu-featured']?.seeAllArticles}
            <img src={TakeArrow} alt="" />
          </Link>
        </Box>
      )}
    </Box>
  );
}

export default Featured;
